import constants from '../../../../qti/Constants';
import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration } from '../../../../services/a11yIIBScreenReaderService';
import { t } from '../../../../services/i18nService';

const keyCodes = constants.keyCodes;

/**
 * @namespace gapChoiceSelector
 * @description Selector utility for handling Gap Choice interaction logic, specifically focused on
 * accessibility feedback and screen reader announcements for drag-and-drop operations.
 */
const gapChoiceSelector = {

    /**
     * Helper function to format movement announcement for a single axis.
     *
     * @function formatAxisMovement
     * @memberof gapChoiceSelector
     * @param {number} movementValue - The movement distance (positive or negative).
     * @param {string} positiveDirectionKey - Translation key for positive movement.
     * @param {string} negativeDirectionKey - Translation key for negative movement.
     * @returns {string|null} The formatted movement text or null if no movement.
     */
    formatAxisMovement: (movementValue, positiveDirectionKey, negativeDirectionKey) => {
        if (movementValue === 0) {
            return null;
        }

        const directionText = movementValue > 0
            ? t(positiveDirectionKey)
            : t(negativeDirectionKey);
        const pixels = Math.round(Math.abs(movementValue));

        return t('interactions.dragAndDrop.movedByPixels', {
            direction: directionText,
            pixels,
        });
    },

    /**
 * Gets the direction key from a keyCode constant.
 *
 * @function getDirectedOnMovingAnnouncementTextFromArrowKeyCode
 * @memberof gapChoiceSelector
 * @param {number} keyCode - The arrow key code (37=LEFT, 38=UP, 39=RIGHT, 40=DOWN).
 * @returns {string} The on moving with direction announcement text or fallback moving announcementText .
 */
    getDirectedOnMovingAnnouncementTextFromArrowKeyCode: (keyCode) => {
        switch (keyCode) {
        case keyCodes.LEFT:
            return t('interactions.dragAndDrop.isMovingLeft');
        case keyCodes.UP:
            return t('interactions.dragAndDrop.isMovingUp');
        case keyCodes.RIGHT:
            return t('interactions.dragAndDrop.isMovingRight');
        case keyCodes.DOWN:
            return t('interactions.dragAndDrop.isMovingDown');
        default:
            return t('interactions.dragAndDrop.isMoving');
        }
    },

    /**
 * Generates an accessibility announcement text for drag handle interactions when movement begins
 * or when direction changes, indicating the direction the item is now moving.
 *
 * @function feedbackAnnouncementOnMovingInDirection
 * @memberof gapChoiceSelector
 * @param {string} textLabel - The text label associated with the draggable item,
 * used to provide context in the announcement. Can be empty or undefined.
 * @param {number} keyCode - The arrow key code pressed (37=LEFT, 38=UP, 39=RIGHT, 40=DOWN).
 * @returns {string} The generated announcement text for screen readers.
 */
    feedbackAnnouncementOnMovingInDirection: (textLabel, keyCode) => {
        let announcement = '';
        if (!a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v4)) {
            return announcement;
        }

        const movingText = gapChoiceSelector.getDirectedOnMovingAnnouncementTextFromArrowKeyCode(keyCode);

        announcement = textLabel ? `${textLabel} ${movingText}` : movingText;
        return announcement;
    },


    /**
 * Generates a complete accessibility announcement text after a sequence of movements has finished,
 * reporting the total distance moved in pixels. This handles multi-directional movement by calculating
 * net X and Y displacement and announcing both directions if moved.
 *
 * @function dragHandleInteractionCompleteFeedbackAnnouncementText
 * @memberof gapChoiceSelector
 * @param {string} textLabel - The text label associated with the draggable item.
 * @param {Object} totalMovement - Object containing net x and y movement in pixels (can be negative).
 * @param {number} totalMovement.x - Net horizontal movement in pixels (positive = right, negative = left).
 * @param {number} totalMovement.y - Net vertical movement in pixels (positive = down, negative = up).
 * @param {Object} finalPosition - Final position after movement.
 * @param {number} finalPosition.x - Final X coordinate.
 * @param {number} finalPosition.y - Final Y coordinate.
 * @returns {string} The complete announcement text for screen readers.
 */
    dragHandleInteractionCompleteFeedbackAnnouncementText: (textLabel, totalMovement, finalPosition) => {
        let announcement = '';
        if (!a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v4)) {
            return announcement;
        }

        if (!totalMovement || (totalMovement.x === 0 && totalMovement.y === 0)) {
            return announcement;
        }

        const movements = [];

        // Handle X-axis movement
        const xMovement = gapChoiceSelector.formatAxisMovement(
            totalMovement.x,
            'interactions.dragAndDrop.movedRight',
            'interactions.dragAndDrop.movedLeft'
        );
        if (xMovement) {
            movements.push(xMovement);
        }

        // Handle Y-axis movement
        const yMovement = gapChoiceSelector.formatAxisMovement(
            totalMovement.y,
            'interactions.dragAndDrop.movedDown',
            'interactions.dragAndDrop.movedUp'
        );
        if (yMovement) {
            movements.push(yMovement);
        }

        if (movements.length > 0) {
            const movementAnnouncement = movements.join(` ${t('interactions.dragAndDrop.andForTwoDirectedMovement')} `);
            const positionAnnouncement = t('interactions.dragAndDrop.currentPosition', {
                xCoordinate: Math.round(finalPosition.x),
                yCoordinate: Math.round(finalPosition.y),
            });
            const genericAnnouncement = `${movementAnnouncement}. ${positionAnnouncement}.`;
            announcement = textLabel ? `${textLabel} ${genericAnnouncement}` : genericAnnouncement;
        }

        return announcement;
    },

    /**
 * Calculates total movement and generates complete feedback announcement.
 * This is a convenience wrapper that combines movement calculation with announcement generation.
 *
 * @function calculateAndAnnounceMovementComplete
 * @memberof gapChoiceSelector
 * @param {string} textLabel - The text label associated with the draggable item.
 * @param {Object} startPosition - The starting position before movement sequence.
 * @param {number} startPosition.x - Starting X coordinate.
 * @param {number} startPosition.y - Starting Y coordinate.
 * @param {Object} finalPosition - Final position after movement.
 * @param {number} finalPosition.x - Final X coordinate.
 * @param {number} finalPosition.y - Final Y coordinate.
 * @returns {string} The complete announcement text for screen readers.
 */
    calculateAndAnnounceMovementComplete: (textLabel, startPosition, finalPosition) => {
        if (!startPosition || !finalPosition) {
            return '';
        }

        const totalMovement = {
            x: finalPosition.x - startPosition.x,
            y: finalPosition.y - startPosition.y,
        };

        return gapChoiceSelector.dragHandleInteractionCompleteFeedbackAnnouncementText(
            textLabel,
            totalMovement,
            finalPosition
        );
    },
};

export default gapChoiceSelector;
